import { useMyEngagementQuery } from '@paintscout/api'
import * as Sentry from '@sentry/react'
import React, { useEffect, useState } from 'react'
import { useConnection } from 'react-detect-offline'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { isE2E } from 'shared/util/e2e'
import AppLoading from 'src/components/App/AppLoading'

/** The flags whose surfaces read `myEngagement` (docs/design/resource-center.md §5). */
export const ENGAGEMENT_FLAGS = ['announcementCenter', 'inHouseSurveys', 'tourManagement'] as const

/**
 * How long the render gate waits for `myEngagement`. After this the app renders anyway and the tour
 * adapter treats every tour as seen until the answer lands, so a slow query never holds the app.
 */
export const ENGAGEMENT_GATE_TIMEOUT_MS = 5000

/**
 * Fires `myEngagement`, the engagement startup query (epic #25305, docs/design/resource-center.md
 * §4), next to `appDocs`. It sits inside the LaunchDarkly provider because it needs the flags:
 * with every engagement flag off, no query runs.
 *
 * Surfaces read the result from the cache (`AppTourProvider` with `cache-only`). The `@wora` cache
 * persists it for offline starts, and `LoadData`'s daily purge re-seeds it.
 *
 * With `tourManagement` on, the app waits for the first answer while online (at most
 * `ENGAGEMENT_GATE_TIMEOUT_MS`), so an auto-start tour never opens before its completion is known.
 * Offline with no cached copy, on an error, or after the timeout, it renders anyway and the tour
 * adapter treats every tour as seen.
 */
export default function MyEngagementLoader({ children }: { children: React.ReactNode }): JSX.Element {
  const flags = useFlags()
  const { online } = useConnection()
  const enabled = ENGAGEMENT_FLAGS.some((flag) => flags[flag] === true) && !isE2E()
  const [gateExpired, setGateExpired] = useState(false)

  const { data, loading, error } = useMyEngagementQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    skip: !enabled
  })

  useEffect(() => {
    const timer = setTimeout(() => setGateExpired(true), ENGAGEMENT_GATE_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    // Without this a broken myEngagement is invisible: every surface just shows nothing.
    if (error) Sentry.captureException(error, { tags: { source: 'MyEngagementLoader' } })
  }, [error])

  const waiting = enabled && flags.tourManagement === true && online && loading && !data?.myEngagement
  if (waiting && !gateExpired) return <AppLoading />
  return <>{children}</>
}
